// ModuleToolbar — floats 12 above the one selected container (pen
// "Modulverktyg", "Studio – detail design" §1/§4): Open, Reorder, Detach part,
// Export, and Undo while its last rename can be taken back (MF-1). Reorder and Detach part each list the parts (the no-drag path for a
// part's grip, D14); every pick is one of the container's part intents.
// Pure; where it sits (viewport pixels) is the canvas's.

export { ModuleToolbar };

import { useLingui } from "@lingui/react/macro";
import type { ReactNode, SyntheticEvent } from "react";
import { Icon } from "../Icon/Icon.js";
import { Menu, type MenuItem } from "../Menu/Menu.js";
import { moveTarget, type PartIntent } from "../ModuleCard/partOps.js";
import type { ContainerCard } from "./studioCards.js";
import type { Point } from "./studioLayout.js";

// The toolbar owns its pointer: a press on it never starts a pan or a drag.
const own = (e: SyntheticEvent) => e.stopPropagation();

const ModuleToolbar = ({
    card,
    at,
    online,
    onOpen,
    onPart,
    onExport,
    onUndo,
}: {
    readonly card: ContainerCard;
    // Its bottom-left corner, in viewport pixels.
    readonly at: Point;
    readonly online: boolean;
    readonly onOpen: () => void;
    readonly onPart: (intent: PartIntent) => void;
    readonly onExport: () => void;
    // Absent: nothing to undo.
    readonly onUndo: (() => void) | undefined;
}): ReactNode => {
    const { t } = useLingui();
    const count = card.parts.length;
    const reorder: readonly MenuItem[] = card.parts.map((part, i): MenuItem => {
        const move = (direction: "up" | "down"): MenuItem => {
            const to = moveTarget(i, count, direction);
            return {
                _tag: "item",
                label: direction === "up" ? t`Move up` : t`Move down`,
                icon: (
                    <Icon
                        name={direction === "up" ? "arrow-up" : "arrow-down"}
                    />
                ),
                disabled: to === undefined,
                onSelect: () =>
                    to === undefined
                        ? undefined
                        : onPart({
                              type: "move",
                              part: part.id,
                              from: i,
                              to,
                          }),
            };
        };
        return {
            _tag: "submenu",
            label: `${i + 1}. ${part.title}`,
            items: [move("up"), move("down")],
        };
    });
    const detach: readonly MenuItem[] = card.parts.map(
        (part, i): MenuItem => ({
            _tag: "item",
            label: `${i + 1}. ${part.title}`,
            onSelect: () => onPart({ type: "remove", part: part.id, at: i }),
        }),
    );
    const tool = (
        icon: "expand" | "reorder" | "unlink" | "export" | "undo",
        label: string,
        disabled: boolean,
        onClick?: () => void,
    ) => (
        <button
            type="button"
            className="bilda-module-toolbar__tool"
            disabled={disabled}
            onClick={onClick}
        >
            <Icon name={icon} size="xs" />
            {label}
        </button>
    );
    return (
        <div
            className="bilda-module-toolbar"
            role="toolbar"
            aria-label={
                card.kind === "course" ? t`Course actions` : t`Module actions`
            }
            style={{ left: at.x, top: at.y }}
            onPointerDown={own}
            onDoubleClick={own}
        >
            {tool("expand", t`Open`, false, onOpen)}
            {count < 2 || !online ? (
                tool("reorder", t`Reorder`, true)
            ) : (
                <Menu
                    items={reorder}
                    trigger={tool("reorder", t`Reorder`, false)}
                />
            )}
            {count === 0 || !online ? (
                tool("unlink", t`Detach part`, true)
            ) : (
                <Menu
                    items={detach}
                    trigger={tool("unlink", t`Detach part`, false)}
                />
            )}
            {tool("export", t`Export`, false, onExport)}
            {onUndo === undefined
                ? null
                : tool("undo", t`Undo`, !online, onUndo)}
        </div>
    );
};
